import React, { ReactElement, useContext } from "react";
import { FormattedMessage } from "react-intl-phraseapp";
import { SearchResultPlan } from "../../@types";
import { makePlanLongId } from "../../helpers";
import { ActionsBar } from "../../components";
import routes from "../../app/routes";
import URI from "urijs";
import { routeReferrerContextMappings } from "../../components/ReferrerDetector";
import { AnalyticsActionType } from "../../app/contexts/Analytics/types";
import { AppContext } from "../../app/store";

interface BackLinkAndPrintProps {
  search: string;
  goBackTextId: string;
  compareList?: SearchResultPlan[];
  icon?: ReactElement;
  nextYearCoverage?: SearchResultPlan | undefined;
  currentCoverage?: SearchResultPlan | undefined;
}

export const getRouteFromBackTextId = (id: string) => {
  const route = Object.keys(routeReferrerContextMappings).find(
    route => routeReferrerContextMappings[route].goBackTextId === id
  );
  // Default to search results route if no other matching route is found
  return route || routes.searchResults;
};

export const getBackToComparePlansUri = (compareList: SearchResultPlan[]) => {
  const compareURI = new URI(routes.comparePlans);
  compareURI.addSearch(
    "plans",
    compareList.map(plan => makePlanLongId(plan))
  );
  return compareURI.toString();
};

export const isSummaryPageRoute = (route: string) =>
  route === routes.summary.landingPage;

interface IGetCameFromCompare
  extends Pick<BackLinkAndPrintProps, "goBackTextId" | "compareList"> {
  checkMissingPlans?: boolean;
}

export const getCameFromCompare = ({
  goBackTextId,
  compareList = [],
  checkMissingPlans = false,
}: IGetCameFromCompare): boolean => {
  return (
    goBackTextId ===
      routeReferrerContextMappings[routes.comparePlans].goBackTextId &&
    (checkMissingPlans ? !compareList.length : !!compareList.length)
  );
};

export const getGoBackFromPlanDetailsValues = ({
  compareList = [],
  search,
  goBackTextId,
  currentCoverage,
  nextYearCoverage,
}: Omit<BackLinkAndPrintProps, "icon">): {
  goBackUri: string;
  LinkContent: () => JSX.Element;
} => {
  const route = getRouteFromBackTextId(goBackTextId);

  let goBackUri = `${routes.searchResults}${search}`;
  let actualGoBackTextId =
    routeReferrerContextMappings[routes.searchResults].goBackTextId;

  if (
    getCameFromCompare({
      goBackTextId,
      compareList,
    })
  ) {
    goBackUri = getBackToComparePlansUri(compareList);
    actualGoBackTextId =
      routeReferrerContextMappings[routes.comparePlans].goBackTextId;
  } else if (
    route === routes.yearOverYear &&
    currentCoverage &&
    nextYearCoverage
  ) {
    const yearOverYearUrl = new URI(routes.yearOverYear);
    yearOverYearUrl.addSearch(
      "plans",
      [currentCoverage, nextYearCoverage].map(plan => makePlanLongId(plan))
    );
    goBackUri = yearOverYearUrl.toString();
    actualGoBackTextId =
      routeReferrerContextMappings[routes.yearOverYear].goBackTextId;
  } else if (isSummaryPageRoute(route)) {
    goBackUri = `${routes.summary.landingPage}${search}`;
    actualGoBackTextId =
      routeReferrerContextMappings[routes.summary.landingPage].goBackTextId;
  }

  return {
    goBackUri,
    LinkContent: () => <FormattedMessage id={actualGoBackTextId} />,
  };
};

export const BackLinkAndPrint: React.FunctionComponent<BackLinkAndPrintProps> = ({
  search,
  goBackTextId,
  compareList = [],
  icon,
  currentCoverage,
  nextYearCoverage,
}) => {
  const { dispatch } = useContext(AppContext);
  const route = getRouteFromBackTextId(goBackTextId);
  const { goBackUri, LinkContent } = getGoBackFromPlanDetailsValues({
    search,
    compareList,
    goBackTextId,
    currentCoverage,
    nextYearCoverage,
  });

  return (
    <ActionsBar
      to={goBackUri}
      onClick={() => {
        if (getCameFromCompare({ goBackTextId, compareList })) {
          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: {
              event_action: "Plan Details",
              event_label: "Back to Plan Compare",
            },
          });
        } else if (isSummaryPageRoute(route)) {
          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: {
              event_action: "Plan Details",
              event_label: "Back to Summary Page",
            },
          });
        } else {
          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: {
              event_action: "Plan Details",
              event_label: "Back to Search Results",
            },
          });
        }
      }}
      printEventAction="Plan Details"
      printClassName="ds-u-display--block ds-u-sm-display--none"
      withPrint
      icon={icon}
    >
      <LinkContent />
    </ActionsBar>
  );
};
